<template>
    <div class="rtb-filters">
        <div class="rtb-filters__statuses" role="radiogroup" aria-label="Статус кампании">
            <button
                type="button"
                role="radio"
                :aria-checked="status === ''"
                :class="['rtb-filters__chip', { 'is-active': status === '' }]"
                @click="status = ''"
            >
                Все
                <span class="rtb-filters__chip-count">{{ totalCount }}</span>
            </button>
            <button
                v-for="option in visibleStatuses"
                :key="option.value"
                type="button"
                role="radio"
                :aria-checked="status === option.value"
                :class="['rtb-filters__chip', `rtb-filters__chip--${toneOf(option.value)}`, { 'is-active': status === option.value }]"
                :title="RTB_STATUS_HINT[option.value]"
                @click="status = option.value"
            >
                {{ option.label }}
                <span class="rtb-filters__chip-count">{{ option.count }}</span>
            </button>
        </div>

        <div class="rtb-filters__fields">
            <label class="rtb-filters__search">
                <span class="visually-hidden">Поиск по названию или ID</span>
                <svg viewBox="0 0 24 24" aria-hidden="true" class="rtb-filters__search-icon">
                    <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2" />
                    <path d="M20 20l-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <input v-model="search" type="search" class="rtb-filters__input" placeholder="Название или ID кампании" />
            </label>

            <label v-if="advertisers.length > 1" class="rtb-filters__field">
                <span class="rtb-filters__label">Рекламодатель</span>
                <select v-model="advertiserId" class="rtb-filters__select">
                    <option value="">Все</option>
                    <option v-for="advertiser in advertisers" :key="advertiser.id" :value="String(advertiser.id)">
                        {{ advertiser.name }}
                    </option>
                </select>
            </label>

            <label class="rtb-filters__field">
                <span class="rtb-filters__label">Сортировка</span>
                <select v-model="sort" class="rtb-filters__select">
                    <option v-for="option in RTB_LIST_SORT_OPTIONS" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
            </label>

            <button v-if="hasActiveFilters" type="button" class="rtb-filters__reset" @click="emit('reset')">
                Сбросить фильтры
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { RTB_LIST_SORT_OPTIONS, RTB_STATUS_HINT, RTB_STATUS_TONE } from '../../../../constants/rtbCampaigns';

const props = defineProps({
    statuses: { type: Array, required: true },
    totalCount: { type: Number, required: true },
    advertisers: { type: Array, default: () => [] },
    hasActiveFilters: { type: Boolean, default: false },
});

const emit = defineEmits(['reset']);

const search = defineModel('search', { type: String, required: true });
const status = defineModel('status', { type: String, required: true });
const advertiserId = defineModel('advertiserId', { type: String, required: true });
const sort = defineModel('sort', { type: String, required: true });

/** Пустые статусы не показываем, кроме выбранного: иначе фильтр «пропадёт» из-под пользователя. */
const visibleStatuses = computed(() => props.statuses.filter(option => option.count > 0 || option.value === status.value));

const toneOf = value => RTB_STATUS_TONE[value] || 'neutral';
</script>

<style scoped lang="scss">
@use '../../../../../scss/pages/admin/admin-tokens' as *;

.rtb-filters {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
}

.rtb-filters__statuses {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-sm;
}

.rtb-filters__chip {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 0.35rem 0.85rem;
    border: 1px solid $admin-border-color;
    border-radius: 999px;
    background: $admin-bg-primary;
    font-size: $font-size-sm;
    font-weight: 500;
    color: $admin-text-secondary;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover { border-color: $admin-border-hover; background: $admin-bg-secondary; }

    &.is-active {
        border-color: $admin-primary-color;
        background: $admin-primary-color;
        color: #fff;

        .rtb-filters__chip-count { background: rgba(255, 255, 255, 0.25); color: #fff; }
    }
}

.rtb-filters__chip-count {
    min-width: 1.4rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background: $admin-bg-hover;
    font-size: $font-size-xs;
    font-weight: 600;
    text-align: center;
    color: $admin-text-muted;
}

.rtb-filters__chip--danger:not(.is-active) .rtb-filters__chip-count { background: $admin-danger-bg; color: $admin-danger-color; }
.rtb-filters__chip--warning:not(.is-active) .rtb-filters__chip-count { background: $admin-warning-bg; color: $admin-warning-color; }

.rtb-filters__fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $spacing-md $spacing-lg;
}

.rtb-filters__search {
    position: relative;
    flex: 1 1 260px;
    max-width: 380px;
    margin: 0;
}

.rtb-filters__search-icon {
    position: absolute;
    top: 50%;
    left: 10px;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    color: $admin-text-light;
    pointer-events: none;
}

.rtb-filters__input,
.rtb-filters__select {
    height: 36px;
    border: 1px solid $admin-border-hover;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    font-size: $font-size-base;
    color: $admin-text-primary;

    &:focus {
        outline: none;
        border-color: $admin-primary-color;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    }
}

.rtb-filters__input {
    width: 100%;
    padding: 0 $spacing-md 0 34px;
}

.rtb-filters__select {
    max-width: 240px;
    padding: 0 $spacing-xl 0 $spacing-md;
}

.rtb-filters__field {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    margin: 0;
}

.rtb-filters__label {
    font-size: $font-size-sm;
    color: $admin-text-muted;
    white-space: nowrap;
}

.rtb-filters__reset {
    padding: 0;
    border: none;
    background: none;
    font-size: $font-size-sm;
    color: $admin-primary-color;
    cursor: pointer;

    &:hover { text-decoration: underline; }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
}
</style>
